export const marketplaceCategories = [
  { key: "Sources", label: "Sources", description: "Connect operational source systems and source objects." },
  { key: "Destinations", label: "Destinations", description: "Deliver governed data to warehouses, files, systems, and endpoints." },
  { key: "DataAndAnalytics", label: "Data and Analytics", description: "Model, query, report, and visualize data in Convoke." },
  { key: "EDI", label: "EDI", description: "Connect trading partners and process inbound or outbound EDI flows." },
  { key: "AI", label: "AI", description: "Connect governed AI clients to approved Convoke tools and data." },
  { key: "Automation", label: "Automation", description: "Schedule work and use purchased execution capacity." }
];

export const toMarketplaceSlug = (displayName) =>
  String(displayName ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const legacyProductSlugRedirects = {
  "api-endpoint": "endpoint",
  "mcp-endpoint": "endpoint"
};

export const PROCESSING_CAPACITY_PRODUCT_KEY = "ProcessingCapacity";

const marketplaceResourceGroupDefinitions = {
  MicrosoftFoundryShared: {
    label: "Microsoft Foundry Shared",
    description: "Lower-cost Convoke-managed Foundry projects isolated by client, environment, and runtime identity."
  },
  MicrosoftFoundryDedicatedConvokeAzure: {
    label: "Microsoft Foundry Dedicated - Convoke Azure",
    description: "Dedicated Convoke-managed Foundry capacity with automated onboarding."
  },
  MicrosoftFoundryDedicatedCustomerAzure: {
    label: "Microsoft Foundry Dedicated - Customer Azure",
    description: "Customer-funded Foundry deployed or connected in the customer Azure boundary."
  },
  OpenAINative: {
    label: "OpenAI Native",
    description: "Customer-funded native OpenAI account configuration that preserves the existing ChatGPT integration path."
  },
  ClaudeNative: {
    label: "Claude Native",
    description: "Future native Claude provider option, gated until stateful-session conformance is approved."
  }
};

const formatMarketplaceGroupKey = (value) => String(value || "")
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .trim();

export const getMarketplaceResourceGroups = (resources = []) => {
  const groupedResources = (resources || []).filter((resource) => resource?.catalogViewGroupKey);
  if (groupedResources.length === 0 || groupedResources.length !== (resources || []).length) {
    return [];
  }

  const groups = new Map();
  groupedResources.forEach((resource) => {
    const key = resource.catalogViewGroupKey;
    if (!groups.has(key)) {
      const definition = marketplaceResourceGroupDefinitions[key] || {};
      groups.set(key, {
        key,
        label: definition.label || formatMarketplaceGroupKey(key),
        description: definition.description || "Related Marketplace Resource packages.",
        resources: []
      });
    }
    groups.get(key).resources.push(resource);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      resources: group.resources.slice().sort((left, right) =>
        Number(left.catalogComparisonOrder || 0) - Number(right.catalogComparisonOrder || 0)
        || String(left.name || "").localeCompare(String(right.name || ""))
      )
    }))
    .sort((left, right) =>
      Math.min(...left.resources.map((resource) => Number(resource.catalogComparisonOrder || 0)))
      - Math.min(...right.resources.map((resource) => Number(resource.catalogComparisonOrder || 0)))
      || left.label.localeCompare(right.label)
    );
};

export const resolveMarketplaceProductSlug = (slug) =>
  legacyProductSlugRedirects[String(slug ?? "").trim().toLowerCase()] || slug;

export const getProductIconKey = (productKey) =>
  `marketplaceproduct${String(productKey ?? "").toLowerCase()}`;

export const getProductIconPath = (productKey) =>
  `/content/marketplace/productIcons/marketplace-product-${productKey}.${productKey === PROCESSING_CAPACITY_PRODUCT_KEY ? "svg" : "png"}`;

export const getResourceIconKey = (resourceKey) =>
  `marketplaceresource${String(resourceKey ?? "").toLowerCase()}`;

const resourceIconPathOverrides = new Map([
  ["inform3source", "/content/marketplace/resourceIcons/marketplace-resource-InforM3Source.svg"],
  ["inform3sync", "/content/marketplace/resourceIcons/marketplace-resource-InforM3Sync.svg"],
  ["pipedrivesource", "/content/marketplace/resourceIcons/marketplace-resource-PipedriveSource.svg"],
  ["powerbireportcatalog", "/content/marketplace/resourceIcons/marketplace-resource-PowerBIReportCatalog.svg"],
  ["ssrsreportcatalog", "/content/marketplace/resourceIcons/marketplace-resource-SSRSReportCatalog.svg"]
]);

export const getResourceIconPath = (resourceKey) => {
  const normalizedResourceKey = String(resourceKey ?? "").trim();
  return resourceIconPathOverrides.get(normalizedResourceKey.toLowerCase())
    || `/content/marketplace/resourceIcons/marketplace-resource-${normalizedResourceKey}.png`;
};

const productDefinitions = [
  {
    key: "SourceIntegration",
    categoryKey: "Sources",
    name: "Source Integration",
    modules: ["Source Systems"],
    summary: "Connect business applications, databases, cloud platforms, and files to Convoke once, then reuse trusted data across dashboards, integrations, automation, and AI.",
    unit: "One configured source-system integration for one provider, environment, and credential boundary."
  },
  {
    key: "WarehouseDestination",
    categoryKey: "Destinations",
    name: "Warehouse Destination",
    modules: ["Warehouse Destinations", "Automation and Batch"],
    summary: "Keep analytics platforms supplied with governed, analysis-ready data through repeatable warehouse loads, validation, and monitoring.",
    unit: "One configured warehouse destination for one environment and target store."
  },
  {
    key: "FileStorageDestination",
    categoryKey: "Destinations",
    name: "File Storage Destination",
    modules: ["File Storage Destinations", "Files and Media"],
    summary: "Deliver business-ready files to FTP-family servers, cloud storage, Microsoft 365, managed trading networks, and AS2 endpoints with reliable scheduling and tracking.",
    unit: "One configured file storage destination for one provider, path, mailbox, or container."
  },
  {
    key: "ExternalSystemSync",
    categoryKey: "Destinations",
    name: "External System Sync",
    modules: ["External System Sync", "Entity Mapping", "Data Definitions", "Automation and Batch"],
    summary: "Keep external applications up to date by mapping, validating, and synchronizing Convoke data into their native business records.",
    unit: "One configured sync profile for one target external system and business sync scope."
  },
  {
    key: "Endpoint",
    categoryKey: "Destinations",
    name: "Endpoint",
    modules: ["Endpoint Publishing", "API Security", "Data Workspaces", "AI Workbench"],
    summary: "Turn governed Convoke data and tools into secure APIs and MCP endpoints that applications and AI assistants can use without direct database access.",
    unit: "One published endpoint surface for one route namespace, data/tool scope, and security policy."
  },
  {
    key: PROCESSING_CAPACITY_PRODUCT_KEY,
    categoryKey: "Automation",
    name: "Processing Capacity",
    modules: ["Automation and Batch", "Private Execution Hosts"],
    summary: "Give scheduled jobs, integrations, endpoints, and batch workloads the dependable runtime capacity they need to run at the right scale.",
    unit: "One purchased runtime capacity grant for scheduled and pool-backed execution."
  },
  {
    key: "ConvokeClientMcpTools",
    categoryKey: "AI",
    name: "Client MCP Tools",
    modules: ["AI Workbench", "API Security", "Data Definitions"],
    summary: "Give approved AI assistants secure access to the client's Convoke knowledge and general tools so users can ask questions and take supported actions in natural language.",
    unit: "One client general MCP tools route for the current Convoke client."
  },
  {
    key: "DataWorkspace",
    categoryKey: "DataAndAnalytics",
    name: "Data Workspace",
    modules: ["Data Workspaces"],
    summary: "Bring data modeling, exploration, querying, and export into one governed workspace where teams can prepare trustworthy data for reuse.",
    unit: "One configured data workspace for one modeling and query scope."
  },
  {
    key: "EntitySchema",
    categoryKey: "DataAndAnalytics",
    name: "Entity Schema",
    modules: ["Data Definitions", "Entity Mapping"],
    summary: "Create a shared business data model that standardizes names, fields, and relationships across sources, integrations, analytics, and AI.",
    unit: "One configured entity schema and its entity objects for one environment and modeling scope."
  },
  {
    key: "DashboardWorkspace",
    categoryKey: "DataAndAnalytics",
    name: "Dashboard Workspace",
    modules: ["Dashboards", "Data Workspaces"],
    summary: "Turn governed business data into interactive dashboards that help operational teams, clients, and executives monitor performance and act faster.",
    unit: "One configured dashboard workspace or dashboard package for one audience."
  },
  {
    key: "ReportCatalog",
    categoryKey: "DataAndAnalytics",
    name: "Report Catalog",
    modules: ["Reports", "App Experience"],
    summary: "Give users one organized, client-ready place to find and view the Power BI, SSRS, and embedded reports relevant to them.",
    unit: "One configured report catalog or landing collection for one audience."
  },
  {
    key: "TradingPartnerConnectivity",
    categoryKey: "EDI",
    name: "Trading Partner Connectivity",
    modules: ["EDI Trading Partners"],
    summary: "Connect trading partners through secure AS2, SFTP, or managed network channels and establish the foundation for reliable EDI exchange.",
    unit: "One configured trading partner connectivity profile."
  },
  {
    key: "InboundEdi",
    categoryKey: "EDI",
    name: "Inbound EDI",
    modules: ["EDI Document Processing", "EDI Routing", "EDI Usage", "Billing and Usage", "Automation and Batch"],
    summary: "Automate incoming orders and other EDI documents from receipt through validation, acknowledgement, routing, and operational visibility.",
    unit: "One configured inbound EDI flow for one standard, partner scope, document set, and routing pattern."
  },
  {
    key: "OutboundEdi",
    categoryKey: "EDI",
    name: "Outbound EDI",
    modules: ["EDI Document Generation", "EDI Routing", "EDI Usage", "Billing and Usage", "Automation and Batch"],
    summary: "Create and deliver compliant invoices, shipment notices, acknowledgements, and other EDI documents from governed business data.",
    unit: "One configured outbound EDI flow for one standard, partner scope, document set, and delivery pattern."
  },
  {
    key: "ClientAppExperience",
    categoryKey: "AppExperience",
    name: "Client App Experience",
    active: false,
    modules: ["App Experience", "Identity and Access"],
    summary: "Give each client a branded, intuitive home for the dashboards, reports, external apps, and Convoke experiences meant for their users.",
    unit: "One configured app experience for one environment and audience."
  }
];

const productMarketingCopyByKey = {
  SourceIntegration: {
    customerOutcome: "turn disconnected operational systems into reusable, governed data sources without rebuilding a connection for every use case",
    buyerFit: "Choose this product when the same source data needs to serve more than one team, workflow, or destination.",
    useCases: [
      "Connect ERP, CRM, marketplace, database, file, and report sources through one governed source catalog.",
      "Reuse the same validated source objects across dashboards, warehouses, integrations, automation, and AI.",
      "Keep production, sandbox, provider, and credential boundaries separately controlled."
    ]
  },
  EntitySchema: {
    customerOutcome: "define a common business language for data that arrives from different systems in different shapes",
    buyerFit: "Choose this product when inconsistent field names and record structures make cross-system reuse difficult or unreliable.",
    useCases: [
      "Standardize customer, product, order, invoice, and other business entities across source systems.",
      "Start from an uploaded, source-discovered, or EDI-generated schema instead of modeling every object by hand.",
      "Version and govern the entity definitions that downstream mappings, dashboards, endpoints, and AI depend on."
    ]
  },
  WarehouseDestination: {
    customerOutcome: "deliver trusted data to warehouse targets with repeatable setup, automated loads, and operational visibility",
    buyerFit: "Choose this product when analysts and reporting tools need refreshed warehouse data without a new custom pipeline for every dataset.",
    useCases: [
      "Load governed data into SQL, document, cloud, and analytics warehouse targets.",
      "Automate recurring full or incremental delivery for reporting and downstream analytics.",
      "Validate target readiness and monitor load results before business users depend on the data."
    ]
  },
  FileStorageDestination: {
    customerOutcome: "send governed files to the storage, collaboration, and trading-network destinations their partners already use",
    buyerFit: "Choose this product when an output must arrive through a specific protocol, folder, mailbox, container, or partner-delivery boundary.",
    useCases: [
      "Deliver partner and customer files through SFTP, FTPS, FTP, AS2, or managed trading networks.",
      "Publish scheduled exports to Azure Blob Storage, SharePoint, or OneDrive folders.",
      "Control file naming, packaging, destination scope, retry behavior, and delivery visibility."
    ]
  },
  ExternalSystemSync: {
    customerOutcome: "push trusted business changes into external systems without sacrificing preview, validation, or control",
    buyerFit: "Choose this product when an ERP, CRM, or other business application must receive ongoing creates or updates from governed Convoke data.",
    useCases: [
      "Synchronize mapped customer, product, order, and other entity data into external applications.",
      "Preview and validate proposed changes before they become operational writes.",
      "Automate repeatable synchronization while retaining operational status and retry visibility."
    ]
  },
  Endpoint: {
    customerOutcome: "give applications and AI clients controlled access to approved data and tools through managed external endpoints",
    buyerFit: "Choose this product when an integration or AI assistant needs a stable external contract but direct source or warehouse access is not acceptable.",
    useCases: [
      "Publish schema-scoped HTTP API routes for approved applications without exposing raw warehouse or source credentials.",
      "Expose approved Convoke tools or governed data-query access to AI clients through MCP.",
      "Use security policy, readiness checks, generated contracts, deployment URLs, and lifecycle controls before activating access."
    ]
  },
  [PROCESSING_CAPACITY_PRODUCT_KEY]: {
    customerOutcome: "match pool-backed workloads to purchased execution capacity before scheduled or on-demand work starts",
    buyerFit: "Choose this product when workloads compete for execution resources or require a predictable, controlled level of runtime scale.",
    useCases: [
      "Select Small, Medium, or Large capacity for scheduled and run-now destination workloads.",
      "Provide capacity for endpoint startup, remote batch execution, and other pool-backed work.",
      "Use Unlimited capacity when approved workloads need pool-agnostic execution without finite grant lease limits."
    ]
  },
  ConvokeClientMcpTools: {
    customerOutcome: "connect approved AI clients to the client's Convoke knowledge and general toolset through one governed MCP surface",
    buyerFit: "Choose this product when users need AI-assisted access to Convoke without building schema-specific API or MCP data deployments.",
    useCases: [
      "Let approved AI clients answer questions from the client's available Convoke knowledge.",
      "Expose the client's general supported Convoke tools through one controlled MCP route.",
      "Keep authentication, client scope, tool availability, and lifecycle management under Convoke governance."
    ]
  },
  DataWorkspace: {
    customerOutcome: "give teams one place to shape, inspect, query, and export governed operational data",
    buyerFit: "Choose this product when analysts, builders, or operators need a reusable working area between raw source data and finished business experiences.",
    useCases: [
      "Model and prepare operational or warehouse-backed data for a defined business scope.",
      "Explore and query governed data without granting direct access to underlying stores.",
      "Publish reusable outputs for dashboards, reports, integrations, exports, and AI."
    ]
  },
  DashboardWorkspace: {
    customerOutcome: "turn governed business data into interactive views tailored to operational, client, or executive audiences",
    buyerFit: "Choose this product when KPIs, trends, and exceptions need to be visible in one audience-ready decision workspace.",
    useCases: [
      "Create operational dashboards for teams monitoring current activity, exceptions, and service levels.",
      "Deliver client-facing dashboards built from governed data without exposing administrative tools.",
      "Package executive scorecards and trends into a focused view for leadership."
    ]
  },
  ReportCatalog: {
    customerOutcome: "organize business reports into a searchable, audience-ready catalog instead of scattered links and portals",
    buyerFit: "Choose this product when reports live across multiple tools or locations and users need a consistent discovery and viewing experience.",
    useCases: [
      "Bring Power BI, SSRS, native, and externally embedded reports into one catalog.",
      "Organize report collections by audience, category, or business purpose.",
      "Give client users a report landing experience without exposing report administration."
    ]
  },
  TradingPartnerConnectivity: {
    customerOutcome: "establish secure, reusable partner connections before inbound and outbound business documents begin flowing",
    buyerFit: "Choose this product when each trading partner needs an approved transport, identity, credential, and delivery boundary.",
    useCases: [
      "Onboard partners through AS2, SFTP, or a managed trading network such as OpenText Trading Grid.",
      "Separate partner-specific connection, credential, and delivery settings from document mappings.",
      "Reuse the approved partner connection across inbound and outbound EDI flows."
    ]
  },
  InboundEdi: {
    customerOutcome: "move incoming EDI documents from receipt to validated, acknowledged, and routed business data",
    buyerFit: "Choose this product when trading-partner documents must be processed consistently at scale instead of handled as files and one-off integrations.",
    useCases: [
      "Receive purchase orders, shipment notices, remittance advice, and other supported EDI documents.",
      "Validate syntax and partner rules, detect duplicates, and generate the required acknowledgements.",
      "Route normalized business data to governed destinations with diagnostics and usage visibility."
    ]
  },
  OutboundEdi: {
    customerOutcome: "turn governed business records into compliant, partner-ready EDI documents and deliver them through approved channels",
    buyerFit: "Choose this product when customers or partners require repeatable outbound documents that conform to agreed standards and delivery rules.",
    useCases: [
      "Generate invoices, shipment notices, acknowledgements, inventory messages, and other supported EDI documents.",
      "Apply partner-specific standards, envelopes, validation, routing, and delivery controls.",
      "Track delivery, diagnostics, acknowledgements, and billable usage across outbound flows."
    ]
  },
  ClientAppExperience: {
    customerOutcome: "create a branded front door for the dashboards, reports, launchers, and workspaces each client should see",
    buyerFit: "Choose this product when client users need a focused experience that reflects their brand and responsibilities instead of an administrative platform menu.",
    useCases: [
      "Create a client landing experience that highlights the most relevant dashboards, reports, and workspaces.",
      "Provide approved launch points for external applications and embedded report experiences.",
      "Tailor navigation and presentation to the audience while preserving Convoke permissions."
    ]
  }
};

const getProductMarketingCopy = (product) => {
  const copy = productMarketingCopyByKey[product?.key];
  if (!copy) {
    throw new Error(`Product marketing copy is required for ProductKey '${product?.key ?? ""}'.`);
  }
  return copy;
};

const buildProductMarketingOverviewHtml = (product) => {
  const copy = getProductMarketingCopy(product);
  const resourceTypes = getProductResourceTypeSummary(product.key);

  if (product.key === PROCESSING_CAPACITY_PRODUCT_KEY) {
    return `
      <section>
        <header>
          <h3>${product.name}</h3>
          <p><strong>${product.name}</strong> helps clients ${copy.customerOutcome}.</p>
        </header>
        <p>${copy.buyerFit}</p>
        <ul>
          <li>Plans are runtime-capacity Product Offers, not Product Resources.</li>
          <li>Small, Medium, and Large grants resolve active pool profiles by pool type.</li>
          <li>Unlimited capacity can use any active pool profile and bypasses finite runtime lease limits.</li>
        </ul>
      </section>
    `;
  }

  return `
    <section>
      <header>
        <h3>${product.name}</h3>
        <p><strong>${product.name}</strong> helps clients ${copy.customerOutcome}.</p>
      </header>
      <p>${copy.buyerFit}</p>
      <ul>
        <li>Supported resource types include ${resourceTypes}.</li>
        <li>Starts with a practical default allowance for configured resources.</li>
        <li>Supports guided setup and review so the client can move from interest to activation with clear ownership.</li>
      </ul>
    </section>
  `;
};

const buildProductTechOverviewHtml = (product) => {
  const modules = product.modules.join(", ");
  return `
    <section>
      <header>
        <h3>Technical overview</h3>
        <p><strong>${product.name}</strong> packages reusable Convoke modules into a system-managed marketplace Product definition.</p>
      </header>
      <p>The product groups resource types for entitlement and setup. Client-specific ownership is represented later through Client Resources and Resource Configurations.</p>
      <ul>
        <li>Includes module coverage for ${modules}.</li>
        <li>Supported resource type overview: ${getProductResourceTypeSummary(product.key)}.</li>
        <li>Resource configuration unit: ${product.unit}</li>
      </ul>
    </section>
  `;
};

const getProductResourceTypeSummary = (productKey) => {
  switch (productKey) {
    case "SourceIntegration":
      return "business application, marketplace, ERP/CRM, database, warehouse, file, and report sources";
    case "WarehouseDestination":
      return "SQL, document, cloud warehouse, and analytics warehouse destinations";
    case "FileStorageDestination":
      return "SFTP, cloud object storage, managed trading-grid, and AS2-backed storage destinations";
    case "ExternalSystemSync":
      return "ERP, CRM, trading-grid, and business-system synchronization resources";
    case "Endpoint":
      return "API endpoint, MCP tool endpoint, and MCP data-query endpoint resources";
    case PROCESSING_CAPACITY_PRODUCT_KEY:
      return "Small, Medium, Large, and Unlimited runtime capacity grants for pool-backed execution";
    case "ConvokeClientMcpTools":
      return "one client-scoped general MCP tools and knowledge route";
    case "DataWorkspace":
      return "operational, warehouse-backed, and semantic data workspace resources";
    case "EntitySchema":
      return "manual, source-started, and EDI-generated entity schema resources";
    case "DashboardWorkspace":
      return "client, operational, and executive dashboard workspace resources";
    case "ReportCatalog":
      return "Power BI and SSRS report catalog resources";
    case "TradingPartnerConnectivity":
      return "AS2, managed trading-grid, and SFTP partner delivery resources";
    case "InboundEdi":
      return "document-specific inbound X12 flows and future EDIFACT inbound resources";
    case "OutboundEdi":
      return "document-specific outbound X12 flows and future EDIFACT outbound resources";
    case "ClientAppExperience":
      return "landing, launcher, and report-entry experience resources";
    default:
      return "marketplace resource options";
  }
};

const buildProductMarketingUseCasesHtml = (product) => {
  const copy = getProductMarketingCopy(product);

  return `
    <section>
      <p>${copy.buyerFit}</p>
      <ul>
        <li>${copy.useCases[0]}</li>
        <li>${copy.useCases[1]}</li>
        <li>${copy.useCases[2]}</li>
      </ul>
    </section>
  `;
};

export const marketplaceProducts = productDefinitions.filter((product) => product.active !== false).map((product) => ({
  ...product,
  Icon: getProductIconKey(product.key),
  IconPath: getProductIconPath(product.key),
  slug: toMarketplaceSlug(product.name),
  MarketingOverviewHtml: buildProductMarketingOverviewHtml(product),
  TechOverviewHtml: buildProductTechOverviewHtml(product),
  MarketingUseCasesHtml: buildProductMarketingUseCasesHtml(product)
}));

const resources = (productKey, names, availability = "available") =>
  names.map((name) => ({ productKey, name, key: name.replace(/[^A-Za-z0-9]+/g, ""), availability }));

const x12ReadyTransactionSetCodes = new Set([
  "810", "850", "855", "856", "860", "846", "940", "945", "820", "943", "944", "824", "997", "999"
]);

const x12TransactionSets = [
  ["180", "Return Merchandise Authorization"],
  ["204", "Motor Carrier Load Tender"],
  ["210", "Motor Carrier Freight Details and Invoice"],
  ["211", "Motor Carrier Bill of Lading"],
  ["214", "Transportation Carrier Shipment Status Message"],
  ["215", "Motor Carrier Pick-up Manifest"],
  ["270", "Health Care Eligibility/Benefit Inquiry"],
  ["271", "Health Care Eligibility/Benefit Information"],
  ["276", "Health Care Claim Status Request"],
  ["277", "Health Care Claim Status Notification"],
  ["310", "Freight Receipt and Invoice"],
  ["315", "Status Details (Ocean)"],
  ["753", "Request for Routing Instructions"],
  ["754", "Routing Instructions"],
  ["810", "Invoice"],
  ["812", "Credit/Debit Adjustment"],
  ["816", "Organizational Relationships"],
  ["820", "Payment Order / Remittance Advice"],
  ["821", "Financial Information Reporting"],
  ["824", "Application Advice"],
  ["830", "Planning Schedule with Release Capability"],
  ["832", "Price/Sales Catalog"],
  ["834", "Benefit Enrollment and Maintenance"],
  ["835", "Health Care Claim Payment/Advice"],
  ["837", "Health Care Claim"],
  ["845", "Price Authorization Acknowledgment / Status"],
  ["846", "Inventory Inquiry/Advice"],
  ["848", "Material Safety Data Sheet"],
  ["850", "Purchase Order"],
  ["852", "Product Activity Data"],
  ["855", "Purchase Order Acknowledgment"],
  ["856", "Advance Ship Notice"],
  ["857", "Shipment and Billing Notice"],
  ["860", "Purchase Order Change Request - Buyer Initiated"],
  ["861", "Receiving Advice / Acceptance Certificate"],
  ["862", "Shipping Schedule"],
  ["865", "Purchase Order Change Acknowledgment / Request"],
  ["867", "Production Sequence"],
  ["870", "Order Status Report"],
  ["875", "Grocery Products Purchase Order"],
  ["876", "Grocery Products Invoice"],
  ["877", "Manufacturer Coupon Redemption Detail"],
  ["878", "Product Authorization/De-authorization"],
  ["879", "Price Information"],
  ["881", "Manufacturer Coupon Redemption Summary"],
  ["882", "Direct Store Delivery Summary Information"],
  ["886", "Customer Call Reporting"],
  ["887", "Coupon Notification"],
  ["888", "Item Maintenance"],
  ["889", "Promotion Announcement"],
  ["940", "Warehouse Shipping Order"],
  ["943", "Warehouse Stock Transfer Shipment Advice"],
  ["944", "Warehouse Stock Transfer Receipt Advice"],
  ["945", "Warehouse Shipping Advice"],
  ["947", "Inventory Adjustment Advice"],
  ["990", "Response to a Load Tender"],
  ["997", "Functional Acknowledgment"],
  ["999", "Implementation Acknowledgment"]
];

const x12EdiResources = (productKey, direction) =>
  x12TransactionSets.map(([code, documentName]) => {
    const name = `X12 ${code} ${documentName} - ${direction} EDI`;
    return {
      productKey,
      name,
      key: `X12${code}${direction}EDI`,
      availability: x12ReadyTransactionSetCodes.has(code) ? "review" : "future"
    };
  });

const getStaticAvailabilityLabel = (availability) => {
  if (availability === "review") return "Available by request";
  if (availability === "admin") return "Contact";
  if (availability === "future") return "Future option";
  return "Available in Convoke";
};

const buildResourceMarketingOverviewHtml = (resource, product) => `
  <section>
    <header>
      <h3>${resource.name}</h3>
      <p><strong>${resource.name}</strong> gives clients a focused way to add ${buildResourceBuyerValue(resource)} under <strong>${product?.name ?? "its product"}</strong>.</p>
    </header>
    <p>This resource option keeps the buyer conversation centered on fit, readiness, and expected operating scope while Convoke handles entitlement, setup, validation, and activation behind the scenes.</p>
    <ul>
      <li>Configuration unit: ${product?.unit ?? "One configured resource option."}</li>
      <li>Availability: ${getStaticAvailabilityLabel(resource.availability)}</li>
      <li>Planning signal: marketplace estimate category only.</li>
    </ul>
  </section>
`;

const buildResourceTechOverviewHtml = (resource, product) => `
  <section>
    <header>
      <h3>Technical overview</h3>
      <p><strong>${resource.name}</strong> is a Product Resource option under <strong>${product?.name ?? "its product"}</strong>.</p>
    </header>
    <p>It represents the concrete provider, runtime, document flow, endpoint, workspace, or experience variant that a Client Resource can entitle and a Resource Configuration can activate.</p>
    <ul>
      <li>Product: ${product?.name ?? resource.productKey}</li>
      <li>Configuration unit: ${product?.unit ?? "One configured resource option."}</li>
      <li>Availability: ${getStaticAvailabilityLabel(resource.availability)}</li>
      <li>Setup and pricing detail are resolved by later marketplace flows.</li>
    </ul>
  </section>
`;

const buildResourceMarketingUseCasesHtml = (resource, product) => `
  <section>
    <p>${resource.name} is a good fit when a client has chosen ${product?.name ?? "this product"} and needs this specific option.</p>
    <ul>
      <li>Present the option in a client-friendly marketplace detail page before any setup workflow begins.</li>
      <li>Route buy, request, or contact decisions through the product resource exposure rules.</li>
      <li>Prepare the option for future Resource Configuration setup without exposing standard admin screens.</li>
    </ul>
  </section>
`;

const buildResourceBuyerValue = (resource) => {
  if (resource?.key === "APIEndpoint") {
    return "secured HTTP API access to selected Convoke data, with generated OpenAPI contract discovery";
  }
  if (resource?.key === "MCPToolEndpoint") {
    return "general Convoke tools and knowledge discovery to AI clients through the client MCP route";
  }
  if (resource?.key === "MCPDataQueryEndpoint") {
    return "governed data-query access to AI clients through MCP";
  }
  if (resource?.key === "ClientMcpToolsEndpoint") {
    return "general Convoke tools and knowledge discovery to AI clients through the client MCP route";
  }
  return "this provider, runtime, destination, document flow, or experience option";
};

const betaResourceKeys = new Set([
  "OdooSource",
  "BusinessCentralSource",
  "GreatPlainsSource",
  "SageIntacctSource",
  "MongoDBSource",
  "SnowflakeSource",
  "GoogleBigQuerySource",
  "ProgressOpenEdgeSource",
  "ConvokeReportSource",
  "AzureSynapseWarehouse",
  "Microsoft365DriveStorage",
  "APIEndpoint",
  "MCPDataQueryEndpoint",
  "ClientMcpToolsEndpoint"
]);

const untaggedResourceKeys = new Set([
  "NetSuiteSource",
  "SalesforceSource",
  "AnaplanSource",
  "SQLServerSource",
  "OneDriveFileSource",
  "PowerBIReportSource",
  "EntitySchema",
  "AzureSQLWarehouse",
  "MongoDBWarehouse",
  "SnowflakeWarehouse",
  "SFTPStorage",
  "AzureBlobStorage"
]);

const futureResourceKeys = new Set([
  "ClientLandingExperience",
  "ExternalAppLauncher",
  "ReportLandingExperience"
]);

export const getFallbackResourceMaturityStatus = (resourceKey) => {
  if (betaResourceKeys.has(resourceKey)) return 2;
  if (untaggedResourceKeys.has(resourceKey)) return 0;
  if (futureResourceKeys.has(resourceKey)) return 3;
  return 1;
};

const resourceDefinitions = [
  ...resources("SourceIntegration", [
    "Odoo Source",
    "Shopify Source",
    "Business Central Source",
    "Salesforce Source",
    "QuickBooks Online Source",
    "SQL Server Source",
    "MongoDB Source",
    "SFTP File Source",
    "Azure Blob File Source",
    "OneDrive File Source",
    "Dropbox File Source"
  ]),
  ...resources("SourceIntegration", [
    "Amazon Seller Source",
    "Great Plains Source",
    "NetSuite Source",
    "Sage Intacct Source",
    "Anaplan Source",
    "Snowflake Source",
    "Google BigQuery Source",
    "Progress OpenEdge Source",
    "OpenText Trading Grid File Source",
    "Power BI Report Source"
  ], "review"),
  ...resources("SourceIntegration", ["Convoke Report Source"], "admin"),
  ...resources("WarehouseDestination", ["Azure SQL Warehouse", "MongoDB Warehouse"]),
  ...resources("WarehouseDestination", ["Snowflake Warehouse", "Azure Synapse Warehouse"], "review"),
  ...resources("FileStorageDestination", ["SFTP Storage", "Azure Blob Storage"]),
  ...resources("FileStorageDestination", ["OpenText Trading Grid Storage", "AS2 Logic Apps Storage"], "review"),
  ...resources("ExternalSystemSync", ["Odoo Sync", "Business Central Sync"]),
  ...resources("ExternalSystemSync", ["Great Plains Sync", "OpenText Trading Grid Sync"], "review"),
  ...resources("Endpoint", ["API Endpoint", "MCP Data Query Endpoint"]),
  { productKey: "ConvokeClientMcpTools", name: "Client MCP Tools", key: "ClientMcpToolsEndpoint", availability: "available", isSingleInstance: true },
  ...resources("DataWorkspace", ["Operational Data Workspace", "Warehouse Data Workspace"]),
  ...resources("DataWorkspace", ["Semantic Data Workspace"], "review"),
  ...resources("EntitySchema", ["Entity Schema"]),
  ...resources("DashboardWorkspace", [
    "Client Dashboard Workspace",
    "Operational Dashboard Workspace",
    "Executive Dashboard Workspace"
  ]),
  ...resources("ReportCatalog", ["SSRS Report Catalog"]),
  ...resources("ReportCatalog", ["Power BI Report Catalog"], "review"),
  ...resources("TradingPartnerConnectivity", ["SFTP Partner Delivery"]),
  ...resources("TradingPartnerConnectivity", ["AS2 Trading Partner", "OpenText Trading Grid Partner"], "review"),
  ...x12EdiResources("InboundEdi", "Inbound"),
  ...resources("InboundEdi", ["EDIFACT Inbound EDI"], "future"),
  ...x12EdiResources("OutboundEdi", "Outbound"),
  ...resources("OutboundEdi", ["EDIFACT Outbound EDI"], "future"),
  ...resources("ClientAppExperience", [
    "Client Landing Experience",
    "External App Launcher",
    "Report Landing Experience"
  ], "inactive")
];

export const marketplaceResources = resourceDefinitions
  .filter((resource) => resource.availability !== "inactive")
  .map((resource) => {
    const product = marketplaceProducts.find((item) => item.key === resource.productKey);
    const usageMetadata = buildFallbackUsageMetadata(resource);
    const enrichedResource = {
      ...resource,
      ...usageMetadata
    };
    return {
      ...enrichedResource,
      maturityStatus: getFallbackResourceMaturityStatus(resource.key),
      Icon: getResourceIconKey(resource.key),
      IconPath: getResourceIconPath(resource.key),
      summary: buildResourceSummary(resource.name, resource.productKey),
      slug: toMarketplaceSlug(resource.name),
      usagePricingDefault: buildFallbackUsagePricing(enrichedResource),
      MarketingOverviewHtml: buildResourceMarketingOverviewHtml(enrichedResource, product),
      TechOverviewHtml: buildResourceTechOverviewHtml(enrichedResource, product),
      MarketingUseCasesHtml: buildResourceMarketingUseCasesHtml(enrichedResource, product)
    };
  });

export const getVisibleResources = (resources = marketplaceResources) =>
  resources.filter((resource) =>
    resource.availability !== "future" || resource.isCatalogUnavailablePreviewVisible
  );

export const getCategory = (categoryKey) =>
  marketplaceCategories.find((category) => category.key === categoryKey);

export const getProduct = (productKey, products = marketplaceProducts) =>
  products.find((product) => product.key === productKey);

export const getProductResources = (productKey, resources = marketplaceResources) =>
  getVisibleResources(resources).filter((resource) => resource.productKey === productKey);

export const getMarketplaceOfferSearchValues = (offers = []) =>
  offers.flatMap((offer) => [offer?.sku, offer?.key, offer?.name]);

export const matchesMarketplaceSearch = (query, values = []) => {
  const normalizedQuery = String(query ?? "").trim().toLowerCase();
  if (!normalizedQuery) return true;

  return values.some((value) =>
    String(value ?? "").toLowerCase().includes(normalizedQuery)
  );
};

export const getAvailabilityLabel = (availability, publicMode = false) => {
  if (availability === "future") return "Preview - unavailable";
  if (availability === "unlocked") return publicMode ? "Available in Convoke" : "Unlocked";
  if (availability === "unlimited") return publicMode ? "Available in Convoke" : "Unlimited";
  if (availability === "review") return publicMode ? "Available by request" : "Review required";
  if (availability === "admin") return publicMode ? "Contact" : "Admin required";
  return publicMode ? "Available in Convoke" : "Available";
};

export const getPrimaryActionLabel = (availability, publicMode = false) => {
  if (publicMode) return availability === "admin" ? "Contact" : "Learn more";
  if (availability === "unlocked") return "Add";
  if (availability === "unlimited") return "Add";
  if (availability === "review") return "Request";
  if (availability === "admin") return "Contact admin";
  return "Buy";
};

const buildSkuParts = (key) => {
  const normalized = String(key || "offer")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  const initials = normalized
    .split("-")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 12) || "PR";
  let hash = 2166136261;
  for (let i = 0; i < normalized.length; i += 1) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const suffix = (hash >>> 0).toString(36).toUpperCase().padStart(8, "0").slice(0, 8);
  return { initials, suffix };
};

const buildFallbackSku = (offerKey, offerKind = 1) => {
  const { initials, suffix } = buildSkuParts(offerKey);
  const skuType = offerKind === 2 ? "LI" : offerKind === 3 ? "UL" : offerKind === 4 ? "RC" : "PR";
  return `CDX-${skuType}-${initials}-${suffix}`;
};

const fallbackSingleOnlyProductKeys = new Set([
  "ReportCatalog",
  "ClientAppExperience"
]);

const isFallbackScaleEligible = (resource) => !fallbackSingleOnlyProductKeys.has(resource?.productKey);

function isInboundOrOutboundEdiResource(resource) {
  return ["InboundEdi", "OutboundEdi"].includes(resource?.productKey);
}

function buildFallbackUsageMetadata(resource) {
  const isEdi = isInboundOrOutboundEdiResource(resource);
  return {
    supportedUsageModes: isEdi ? [5, 6] : [6],
    defaultUsageUnitMode: isEdi ? 5 : 6,
    usageCountingRule: isEdi
      ? "One billable transaction is one complete successful inbound or outbound EDI file process through the system."
      : "One billable configuration is one non-retired Resource Configuration created under the purchased Client Resource entitlement.",
    usageAttributionRequirement: isEdi
      ? "Usage must resolve to the purchased Client Resource or Resource Configuration and include the Product Resource key, processing direction, and source file or event identity."
      : "Usage is counted from Resource Configuration records linked by ClientResourceId to the purchased Client Resource entitlement."
  };
}

function buildFallbackUsagePricing(resource) {
  const mode = resource?.defaultUsageUnitMode || buildFallbackUsageMetadata(resource).defaultUsageUnitMode;
  const unitLabels = {
    1: "documents",
    2: "requests",
    3: "files",
    4: "prompts",
    5: "transactions",
    6: "configurations"
  };
  return {
    usagePricingEnabled: resource.availability !== "review" && resource.availability !== "admin",
    usageUnitMode: mode,
    unitLabel: unitLabels[mode],
    includedUsageUnits: 0,
    unitRate: 0,
    currencyCode: "USD",
    billingPeriod: 1,
    priceSummary: `Usage pricing is configured by marketplace Product Offers using ${unitLabels[mode]} as the default unit.`
  };
}

const buildFallbackOffer = (resource, plan, index) => ({
  key: plan === "scale" ? `${resource.key}ScaleOffer` : `${resource.key}StandardOffer`,
  name: `${resource.name} ${plan === "scale" ? "Scale" : "Single"}`,
  sku: buildFallbackSku(plan === "scale" ? `${resource.key}ScaleOffer` : `${resource.key}StandardOffer`),
  offerKind: 1,
  priceSummary: resource.availability === "review" || resource.availability === "admin"
    ? "Final pricing and activation require review."
    : `${plan === "scale" ? "Scale" : "Single"} marketplace package.`,
  currencyCode: "USD",
  billingPeriod: 1,
  baseAmount: 0,
  includedUnits: 0,
  unitRate: 0,
  unitLabel: "configurations",
  sortOrder: index,
  resourceGrants: [
    {
      productResourceKey: resource.key,
      allowedResourceConfigurationCount: plan === "scale" ? 99 : 1,
      grantRole: 1,
      useProductResourceUsageDefaults: true,
      ...buildFallbackUsagePricing(resource),
      usageCountingRule: resource.usageCountingRule,
      usageAttributionRequirement: resource.usageAttributionRequirement
    }
  ],
  placements: [
    {
      showOnProductResourceKey: resource.key,
      displayLabel: resource.name,
      sortOrder: 1
    }
  ],
  MarketingOverviewHtml: "",
  TechOverviewHtml: ""
});

const buildFallbackOffers = (resource, index) => {
  const baseSort = index * 2;
  const offers = [buildFallbackOffer(resource, "single", baseSort + 1)];
  if (isFallbackScaleEligible(resource)) {
    offers.push(buildFallbackOffer(resource, "scale", baseSort + 2));
  }
  return offers;
};

const buildFallbackRuntimeCapacityOffer = ({ key, name, poolType, vmSize, unlimited, maxConcurrentRuns = 1, baseAmount = 0 }, index) => {
  const normalizedMaxConcurrentRuns = unlimited ? 1 : Math.max(1, Number(maxConcurrentRuns || 1));
  const sizeName = String(name || "").replace(" Processing Capacity", "").replace(/ x[35]$/i, "").toLowerCase();
  const bulkDiscount = !unlimited && normalizedMaxConcurrentRuns > 1
    ? normalizedMaxConcurrentRuns === 3
      ? " Includes a 10% bulk discount."
      : " Includes a 20% bulk discount."
    : "";

  return {
    key,
    name,
    sku: buildFallbackSku(key, 4),
    offerKind: 4,
    priceSummary: unlimited
      ? "Unlimited processing capacity; commercial terms are managed at the Product Offer level."
      : `${normalizedMaxConcurrentRuns} active ${sizeName} background ${normalizedMaxConcurrentRuns === 1 ? "run" : "runs"} at a time; default VM size ${vmSize}.${bulkDiscount}`,
    currencyCode: "USD",
    billingPeriod: 1,
    baseAmount: unlimited ? 0 : Number(baseAmount || 0),
    includedUnits: unlimited ? 2147483647 : normalizedMaxConcurrentRuns,
    unitRate: 0,
    unitLabel: unlimited ? "runtime capacity" : normalizedMaxConcurrentRuns === 1 ? "active run" : "active runs",
    sortOrder: 10000 + index,
    reviewRequired: Boolean(unlimited),
    customPricing: Boolean(unlimited),
    resourceGrants: [],
    capacityGrants: [
      {
        capacityGrantKey: key,
        displayName: name,
        isUnlimited: unlimited,
        poolType,
        poolProfileId: "",
        maxConcurrentRuns: normalizedMaxConcurrentRuns,
        runtimeScope: "AzureBatch",
        isActive: true,
        sortOrder: 1,
        notes: unlimited
          ? "Unlimited runtime capacity can use any active pool profile and bypasses finite runtime lease checks."
          : `Finite runtime capacity resolves an active pool profile for pool type ${poolType} (${vmSize}) and allows ${normalizedMaxConcurrentRuns} active ${normalizedMaxConcurrentRuns === 1 ? "run" : "runs"} for the selected grant and resolved pool profile.`
      }
    ],
    placements: [],
    MarketingOverviewHtml: "",
    TechOverviewHtml: ""
  };
};

const fallbackRuntimeCapacityOffers = [
  { key: "SmallProcessingCapacity", name: "Small Processing Capacity", poolType: 1, vmSize: "Standard_A1_v2", unlimited: false, maxConcurrentRuns: 1, baseAmount: 100 },
  { key: "SmallProcessingCapacity3x", name: "Small Processing Capacity x3", poolType: 1, vmSize: "Standard_A1_v2", unlimited: false, maxConcurrentRuns: 3, baseAmount: 270 },
  { key: "SmallProcessingCapacity5x", name: "Small Processing Capacity x5", poolType: 1, vmSize: "Standard_A1_v2", unlimited: false, maxConcurrentRuns: 5, baseAmount: 400 },
  { key: "MediumProcessingCapacity", name: "Medium Processing Capacity", poolType: 2, vmSize: "Standard_A2_v2", unlimited: false, maxConcurrentRuns: 1, baseAmount: 250 },
  { key: "MediumProcessingCapacity3x", name: "Medium Processing Capacity x3", poolType: 2, vmSize: "Standard_A2_v2", unlimited: false, maxConcurrentRuns: 3, baseAmount: 675 },
  { key: "MediumProcessingCapacity5x", name: "Medium Processing Capacity x5", poolType: 2, vmSize: "Standard_A2_v2", unlimited: false, maxConcurrentRuns: 5, baseAmount: 1000 },
  { key: "LargeProcessingCapacity", name: "Large Processing Capacity", poolType: 3, vmSize: "Standard_A4_v2", unlimited: false, maxConcurrentRuns: 1, baseAmount: 500 },
  { key: "LargeProcessingCapacity3x", name: "Large Processing Capacity x3", poolType: 3, vmSize: "Standard_A4_v2", unlimited: false, maxConcurrentRuns: 3, baseAmount: 1350 },
  { key: "LargeProcessingCapacity5x", name: "Large Processing Capacity x5", poolType: 3, vmSize: "Standard_A4_v2", unlimited: false, maxConcurrentRuns: 5, baseAmount: 2000 },
  { key: "UnlimitedProcessingCapacity", name: "Unlimited Processing Capacity", poolType: null, vmSize: "", unlimited: true }
].map(buildFallbackRuntimeCapacityOffer);

const attachRuntimeCapacityOffersToProducts = (products, productOffers) => {
  const runtimeCapacityOffers = (productOffers || [])
    .filter((offer) => Number(offer.offerKind ?? offer.OfferKind ?? 0) === 4)
    .sort((left, right) =>
      (Number(left.sortOrder ?? left.SortOrder ?? 0) - Number(right.sortOrder ?? right.SortOrder ?? 0))
      || String(left.name ?? left.DisplayName ?? "").localeCompare(String(right.name ?? right.DisplayName ?? ""))
    );
  const hasProcessingCapacityProduct = (products || []).some((product) =>
    product.key === PROCESSING_CAPACITY_PRODUCT_KEY
  );
  const processingCapacityFallbackProduct = marketplaceProducts.find((product) =>
    product.key === PROCESSING_CAPACITY_PRODUCT_KEY
  );
  const sourceProducts = !hasProcessingCapacityProduct && runtimeCapacityOffers.length > 0 && processingCapacityFallbackProduct
    ? [...(products || []), processingCapacityFallbackProduct]
    : (products || []);

  return sourceProducts.map((product) =>
    product.key === PROCESSING_CAPACITY_PRODUCT_KEY
      ? { ...product, offers: runtimeCapacityOffers }
      : product
  );
};

export const marketplaceProductOffers = marketplaceResources
  .filter((resource) => resource.availability !== "future")
  .flatMap(buildFallbackOffers)
  .concat(fallbackRuntimeCapacityOffers);

const getFallbackOffersForResource = (resourceKey) =>
  marketplaceProductOffers.filter((offer) => {
    if ((offer.placements || []).length > 0) {
      return offer.placements.some((placement) => placement.showOnProductResourceKey === resourceKey);
    }

    return (offer.resourceGrants || []).some((grant) =>
      grant.productResourceKey === resourceKey && grant.grantRole === 1
    );
  }).sort((left, right) =>
    (left.sortOrder || 0) - (right.sortOrder || 0)
    || String(left.name).localeCompare(String(right.name))
  );

export const fallbackMarketplaceCatalog = {
  categories: marketplaceCategories,
  products: attachRuntimeCapacityOffersToProducts(marketplaceProducts, marketplaceProductOffers),
  resources: marketplaceResources.map((resource) => ({
    ...resource,
    offers: getFallbackOffersForResource(resource.key)
  })),
  productOffers: marketplaceProductOffers,
  clientProductOffers: [],
  clientResources: [],
  resourceConfigurations: []
};

const normalizeString = (...values) =>
  values.find((value) => typeof value === "string" && value.trim().length > 0)?.trim() ?? "";

const UNLIMITED_RESOURCES_OFFER_KEY = "UnlimitedResources";
const UNLIMITED_RESOURCES_DISPLAY_NAME = "Unlimited Resources";
const UNLIMITED_RESOURCES_SKU = "CDX-UL-UR-4C0822AD";

const unlimitedResourcesIdentifierAliases = [
  "enterpriseunlimitedpackage",
  "enterpriseunlimitedpackageoffer",
  "enterpriseunlimitedpackageproductoffer",
  "unlimitedmarketplace",
  "unlimitedmarkeplace",
  "unlimitedmarkplace",
  "unlimitedmarketplaceoffer",
  "unlimitedmarkeplaceoffer",
  "unlimitedmarkplaceoffer",
  "unlimitedmarketplaceproductoffer",
  "unlimitedmarkeplaceproductoffer",
  "unlimitedmarkplaceproductoffer",
  "unlimitedresources",
  "unlimitedresourcesoffer",
  "unlimitedresourcesproductoffer"
];

const compactIdentifier = (value) =>
  String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

const isUnlimitedResourcesLabelVariant = (value) => {
  const compact = compactIdentifier(value);
  return unlimitedResourcesIdentifierAliases.includes(compact);
};

const normalizeUnlimitedResourcesLabel = (...values) => {
  const value = normalizeString(...values);
  if (!value) return "";
  if (isUnlimitedResourcesLabelVariant(value)) return UNLIMITED_RESOURCES_DISPLAY_NAME;
  return value.replace(
    /\bUnlimited\s+(?:Market\s*place|Marketplace|Markeplace|Markplace)(?:\s+(?:Product\s+)?Offer)?\b/gi,
    UNLIMITED_RESOURCES_DISPLAY_NAME
  );
};

const isUnlimitedResourcesOffer = (offerKind, ...identifiers) =>
  Number(offerKind) === 3 || identifiers.some(isUnlimitedResourcesLabelVariant);

const normalizeProductOfferKey = (productOfferKey, offerKind) =>
  isUnlimitedResourcesOffer(offerKind, productOfferKey)
    ? UNLIMITED_RESOURCES_OFFER_KEY
    : normalizeString(productOfferKey);

const normalizeProductOfferSku = (productOfferSku, productOfferKey, offerKind) =>
  isUnlimitedResourcesOffer(offerKind, productOfferKey)
    ? UNLIMITED_RESOURCES_SKU
    : normalizeString(productOfferSku);

const normalizeArray = (...values) =>
  values.find((value) => Array.isArray(value)) ?? [];

const normalizeNumber = (...values) => {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== "");
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeBoolean = (...values) => {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null);
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
};

const normalizeUsagePricing = (...values) => {
  const pricing = values.find((value) => value && typeof value === "object") ?? {};
  return {
    usagePricingEnabled: normalizeBoolean(pricing.UsagePricingEnabled, pricing.usagePricingEnabled),
    usageUnitMode: normalizeNumber(pricing.UsageUnitMode, pricing.usageUnitMode),
    unitLabel: normalizeString(pricing.UnitLabel, pricing.unitLabel),
    includedUsageUnits: normalizeNumber(pricing.IncludedUsageUnits, pricing.includedUsageUnits) || 0,
    unitRate: normalizeNumber(pricing.UnitRate, pricing.unitRate) || 0,
    currencyCode: normalizeString(pricing.CurrencyCode, pricing.currencyCode) || "USD",
    billingPeriod: normalizeNumber(pricing.BillingPeriod, pricing.billingPeriod) || 1,
    priceSummary: normalizeString(pricing.PriceSummary, pricing.priceSummary, pricing.UsagePriceSummary, pricing.usagePriceSummary)
  };
};

const resolveBackendAvailability = (resource, catalog) => {
  const releaseStatus = normalizeNumber(resource.ReleaseStatus, resource.releaseStatus);
  if (releaseStatus === 3) return "future";

  const deploymentGroup = normalizeNumber(catalog?.ExposureDeploymentGroup, catalog?.exposureDeploymentGroup);
  const exposureDefaults = normalizeArray(resource.ExposureDefaults, resource.exposureDefaults);
  const matchedDefault = exposureDefaults.find((entry) =>
    normalizeNumber(entry.DeploymentGroup, entry.deploymentGroup) === deploymentGroup
  );
  const exposure = normalizeNumber(
    matchedDefault?.Exposure,
    matchedDefault?.exposure,
    resource.ApprovalDefault,
    resource.approvalDefault
  );
  const marketplaceAccessMode = normalizeNumber(catalog?.MarketplaceAccessMode, catalog?.marketplaceAccessMode);
  const isMarketplaceUnlimited =
    marketplaceAccessMode === 2 ||
    normalizeBoolean(catalog?.IsMarketplaceUnlimited, catalog?.isMarketplaceUnlimited);
  const isMarketplaceAdmin = normalizeBoolean(catalog?.IsMarketplaceAdmin, catalog?.isMarketplaceAdmin);

  if (exposure === 4) return "future";
  if (exposure === 3) return isMarketplaceAdmin ? "unlimited" : "admin";
  if (isMarketplaceUnlimited && (exposure === 1 || exposure === 2)) return "unlimited";
  if (exposure === 2) return "review";
  return "available";
};

export const normalizeMarketplaceCatalog = (catalog) => {
  const rawProducts = normalizeArray(catalog?.Products, catalog?.products);
  const rawResources = normalizeArray(catalog?.ProductResources, catalog?.productResources);
  const rawOffers = normalizeArray(catalog?.ProductOffers, catalog?.productOffers);
  const rawClientProductOffers = normalizeArray(catalog?.ClientProductOffers, catalog?.clientProductOffers);
  const rawClientResources = normalizeArray(catalog?.ClientResources, catalog?.clientResources);
  const rawResourceConfigurations = normalizeArray(catalog?.ResourceConfigurations, catalog?.resourceConfigurations);
  const rawEffectiveResourceAllowances = normalizeArray(catalog?.EffectiveResourceAllowances, catalog?.effectiveResourceAllowances);

  if (rawProducts.length === 0 || rawResources.length === 0) {
    return fallbackMarketplaceCatalog;
  }

  const products = rawProducts.map((product) => {
    const id = normalizeString(product._id, product.Id, product.id, product.UniqueId, product.uniqueId);
    const productKey = normalizeString(product.ProductKey, product.productKey, product.key);
    const name = normalizeString(product.DisplayName, product.displayName, product.name);
    return {
      id,
      _id: id,
      key: productKey,
      categoryKey: normalizeString(product.CategoryKey, product.categoryKey),
      name,
      modules: normalizeArray(product.ModuleKeys, product.moduleKeys, product.modules),
      summary: normalizeString(product.Description, product.description, product.summary),
      unit: normalizeString(product.ResourceConfigurationUnit, product.resourceConfigurationUnit, product.unit),
      Icon: normalizeString(product.Icon, product.icon) || getProductIconKey(productKey),
      IconPath: getProductIconPath(productKey),
      slug: toMarketplaceSlug(name),
      MarketingOverviewHtml: normalizeString(product.MarketingOverviewHtml, product.marketingOverviewHtml),
      TechOverviewHtml: normalizeString(product.TechOverviewHtml, product.techOverviewHtml),
      MarketingUseCasesHtml: normalizeString(product.MarketingUseCasesHtml, product.marketingUseCasesHtml)
    };
  });

  const productOffers = rawOffers.map((offer) => {
    const id = normalizeString(offer._id, offer.Id, offer.id, offer.UniqueId, offer.uniqueId);
    const offerKind = normalizeNumber(offer.OfferKind, offer.offerKind);
    const offerKey = normalizeProductOfferKey(
      normalizeString(offer.ProductOfferKey, offer.productOfferKey, offer.key),
      offerKind
    );
    const name = normalizeUnlimitedResourcesLabel(offer.DisplayName, offer.displayName, offer.name);
    return {
      id,
      _id: id,
      key: offerKey,
      name,
      sku: normalizeProductOfferSku(normalizeString(offer.Sku, offer.sku), offerKey, offerKind) || offerKey,
      offerKind,
      priceSummary: normalizeUnlimitedResourcesLabel(offer.PriceSummary, offer.priceSummary),
      currencyCode: normalizeString(offer.CurrencyCode, offer.currencyCode) || "USD",
      billingPeriod: normalizeNumber(offer.BillingPeriod, offer.billingPeriod) || 1,
      baseAmount: normalizeNumber(offer.BaseAmount, offer.baseAmount) || 0,
      includedUnits: normalizeNumber(offer.IncludedUnits, offer.includedUnits) || 0,
      unitRate: normalizeNumber(offer.UnitRate, offer.unitRate) || 0,
      unitLabel: normalizeString(offer.UnitLabel, offer.unitLabel),
      sortOrder: normalizeNumber(offer.SortOrder, offer.sortOrder) || 0,
      reviewRequired: normalizeBoolean(offer.ReviewRequired, offer.reviewRequired),
      customPricing: normalizeBoolean(offer.CustomPricing, offer.customPricing),
      resourceGrants: normalizeArray(offer.ResourceGrants, offer.resourceGrants).map((grant) => ({
        productResourceKey: normalizeString(grant.ProductResourceKey, grant.productResourceKey),
        allowedResourceConfigurationCount: normalizeNumber(
          grant.AllowedResourceConfigurationCount,
          grant.allowedResourceConfigurationCount
        ) || 1,
        grantRole: normalizeNumber(grant.GrantRole, grant.grantRole),
        useProductResourceUsageDefaults: normalizeBoolean(
          grant.UseProductResourceUsageDefaults,
          grant.useProductResourceUsageDefaults
        ),
        ...normalizeUsagePricing(grant),
        usageCountingRule: normalizeString(grant.UsageCountingRule, grant.usageCountingRule),
        usageAttributionRequirement: normalizeString(grant.UsageAttributionRequirement, grant.usageAttributionRequirement),
        notes: normalizeString(grant.Notes, grant.notes)
      })),
      capacityGrants: normalizeArray(offer.CapacityGrants, offer.capacityGrants).map((grant) => ({
        capacityGrantKey: normalizeString(grant.CapacityGrantKey, grant.capacityGrantKey),
        displayName: normalizeString(grant.DisplayName, grant.displayName),
        isUnlimited: normalizeBoolean(grant.IsUnlimited, grant.isUnlimited),
        poolType: normalizeNumber(grant.PoolType, grant.poolType),
        poolProfileId: normalizeString(grant.PoolProfileId, grant.poolProfileId),
        maxConcurrentRuns: normalizeNumber(grant.MaxConcurrentRuns, grant.maxConcurrentRuns) || 0,
        runtimeScope: normalizeString(grant.RuntimeScope, grant.runtimeScope),
        isActive: normalizeBoolean(grant.IsActive, grant.isActive),
        sortOrder: normalizeNumber(grant.SortOrder, grant.sortOrder) || 0,
        notes: normalizeString(grant.Notes, grant.notes)
      })),
      placements: normalizeArray(offer.Placements, offer.placements).map((placement) => ({
        showOnProductResourceKey: normalizeString(
          placement.ShowOnProductResourceKey,
          placement.showOnProductResourceKey
        ),
        displayLabel: normalizeUnlimitedResourcesLabel(placement.DisplayLabel, placement.displayLabel),
        sortOrder: normalizeNumber(placement.SortOrder, placement.sortOrder) || 0
      })),
      MarketingOverviewHtml: normalizeString(offer.MarketingOverviewHtml, offer.marketingOverviewHtml),
      TechOverviewHtml: normalizeString(offer.TechOverviewHtml, offer.techOverviewHtml)
    };
  });

  const clientProductOffers = rawClientProductOffers.map((clientOffer) => {
    const id = normalizeString(clientOffer._id, clientOffer.Id, clientOffer.id, clientOffer.UniqueId, clientOffer.uniqueId);
    const offerKind = normalizeNumber(clientOffer.OfferKind, clientOffer.offerKind);
    const productOfferKey = normalizeProductOfferKey(
      normalizeString(clientOffer.ProductOfferKey, clientOffer.productOfferKey),
      offerKind
    );
    return {
      id,
      _id: id,
      productOfferKey,
      sku: normalizeProductOfferSku(normalizeString(clientOffer.Sku, clientOffer.sku), productOfferKey, offerKind),
      displayName: normalizeUnlimitedResourcesLabel(clientOffer.DisplayName, clientOffer.displayName),
      offerKind,
      quantity: normalizeNumber(clientOffer.Quantity, clientOffer.quantity) || 1,
      lifecycleStatus: normalizeNumber(clientOffer.LifecycleStatus, clientOffer.lifecycleStatus),
      approvalStatus: normalizeNumber(clientOffer.ApprovalStatus, clientOffer.approvalStatus),
      resourceGrants: normalizeArray(clientOffer.ResourceGrantsSnapshot, clientOffer.resourceGrantsSnapshot).map((grant) => ({
        productResourceKey: normalizeString(grant.ProductResourceKey, grant.productResourceKey),
        allowedResourceConfigurationCount: normalizeNumber(
          grant.AllowedResourceConfigurationCount,
          grant.allowedResourceConfigurationCount
        ) || 1,
        useProductResourceUsageDefaults: normalizeBoolean(
          grant.UseProductResourceUsageDefaults,
          grant.useProductResourceUsageDefaults
        ),
        ...normalizeUsagePricing(grant),
        usageCountingRule: normalizeString(grant.UsageCountingRule, grant.usageCountingRule),
        usageAttributionRequirement: normalizeString(grant.UsageAttributionRequirement, grant.usageAttributionRequirement)
      })),
      capacityGrants: normalizeArray(clientOffer.CapacityGrantsSnapshot, clientOffer.capacityGrantsSnapshot).map((grant) => ({
        capacityGrantKey: normalizeString(grant.CapacityGrantKey, grant.capacityGrantKey),
        displayName: normalizeString(grant.DisplayName, grant.displayName),
        isUnlimited: normalizeBoolean(grant.IsUnlimited, grant.isUnlimited),
        poolType: normalizeNumber(grant.PoolType, grant.poolType),
        poolProfileId: normalizeString(grant.PoolProfileId, grant.poolProfileId),
        maxConcurrentRuns: normalizeNumber(grant.MaxConcurrentRuns, grant.maxConcurrentRuns) || 0,
        runtimeScope: normalizeString(grant.RuntimeScope, grant.runtimeScope),
        isActive: normalizeBoolean(grant.IsActive, grant.isActive),
        sortOrder: normalizeNumber(grant.SortOrder, grant.sortOrder) || 0,
        notes: normalizeString(grant.Notes, grant.notes)
      }))
    };
  }).filter((clientOffer) => clientOffer.productOfferKey);

  const activeClientProductOffers = clientProductOffers.filter((offer) =>
    offer.approvalStatus === 3 && ![8, 9].includes(offer.lifecycleStatus)
  );
  const unlimitedOfferActive = activeClientProductOffers.some((offer) => offer.offerKind === 3);
  const marketplaceAccessMode = normalizeNumber(catalog?.MarketplaceAccessMode, catalog?.marketplaceAccessMode);
  const catalogUnlimitedAccess =
    marketplaceAccessMode === 2 ||
    normalizeBoolean(catalog?.IsMarketplaceUnlimited, catalog?.isMarketplaceUnlimited);
  const isSelfHostedResourceMode = marketplaceAccessMode === 2
    || normalizeBoolean(catalog?.IsSelfHostedResourceMode, catalog?.isSelfHostedResourceMode);
  const hasUnlimitedResourcesAccess = catalogUnlimitedAccess || unlimitedOfferActive;
  const unlockedResourceKeys = new Set();
  activeClientProductOffers.forEach((offer) => {
    offer.resourceGrants.forEach((grant) => {
      if (grant.productResourceKey) {
        unlockedResourceKeys.add(grant.productResourceKey);
      }
    });
  });

  const normalizePurchasedOfferAllowances = (allowance) =>
    normalizeArray(allowance.PurchasedOffers, allowance.purchasedOffers, allowance.Grants, allowance.grants)
      .map((offer) => {
        const offerKind = normalizeNumber(offer.OfferKind, offer.offerKind);
        const productOfferKey = normalizeProductOfferKey(
          normalizeString(offer.ProductOfferKey, offer.productOfferKey),
          offerKind
        );
        return {
          clientProductOfferId: normalizeString(
            offer.ClientProductOfferId,
            offer.clientProductOfferId
          ),
          productOfferKey,
          productOfferSku: normalizeProductOfferSku(
            normalizeString(offer.ProductOfferSku, offer.productOfferSku),
            productOfferKey,
            offerKind
          ),
          displayName: normalizeUnlimitedResourcesLabel(offer.DisplayName, offer.displayName),
          productResourceKey: normalizeString(offer.ProductResourceKey, offer.productResourceKey),
          allowedCount: normalizeNumber(offer.AllowedCount, offer.allowedCount) || 0,
          assignedCount: normalizeNumber(offer.AssignedCount, offer.assignedCount) || 0,
          remainingCount: normalizeNumber(offer.RemainingCount, offer.remainingCount) || 0,
          quantity: normalizeNumber(offer.Quantity, offer.quantity) || 1,
          isUnlimited: normalizeBoolean(offer.IsUnlimited, offer.isUnlimited)
        };
      })
      .filter((offer) => offer.clientProductOfferId || offer.productOfferKey || offer.displayName);

  const effectiveResourceAllowances = rawEffectiveResourceAllowances.map((allowance) => ({
    clientResourceId: normalizeString(allowance.ClientResourceId, allowance.clientResourceId),
    productResourceKey: normalizeString(allowance.ProductResourceKey, allowance.productResourceKey),
    allowedCount: normalizeNumber(allowance.AllowedCount, allowance.allowedCount) || 0,
    reservedCount: normalizeNumber(allowance.ReservedCount, allowance.reservedCount) || 0,
    remainingCount: normalizeNumber(allowance.RemainingCount, allowance.remainingCount) || 0,
    isUnlimited: normalizeBoolean(allowance.IsUnlimited, allowance.isUnlimited),
    usesLegacyFallback: normalizeBoolean(allowance.UsesLegacyFallback, allowance.usesLegacyFallback),
    hasActiveGrantData: normalizeBoolean(allowance.HasActiveGrantData, allowance.hasActiveGrantData),
    purchasedOffers: normalizePurchasedOfferAllowances(allowance)
  })).filter((allowance) => allowance.clientResourceId);
  const effectiveAllowanceByClientResourceId = new Map(effectiveResourceAllowances.map((allowance) => [
    allowance.clientResourceId,
    allowance
  ]));

  const getOffersForResource = (resourceKey) =>
    productOffers.filter((offer) => {
      if (offer.placements.length > 0) {
        return offer.placements.some((placement) => placement.showOnProductResourceKey === resourceKey);
      }
      return offer.resourceGrants.some((grant) => grant.productResourceKey === resourceKey && grant.grantRole === 1);
    }).sort((left, right) =>
      (left.sortOrder || 0) - (right.sortOrder || 0)
      || String(left.name).localeCompare(String(right.name))
    );

  const resources = rawResources.map((resource) => {
    const id = normalizeString(resource._id, resource.Id, resource.id, resource.UniqueId, resource.uniqueId);
    const productKey = normalizeString(resource.ProductKey, resource.productKey);
    const resourceKey = normalizeString(resource.ProductResourceKey, resource.productResourceKey, resource.key);
    const name = normalizeString(resource.DisplayName, resource.displayName, resource.name);
    const product = getProduct(productKey, products);
    const backendAvailability = resolveBackendAvailability(resource, catalog);
    const isUnlimited = backendAvailability === "unlimited"
      || (hasUnlimitedResourcesAccess && backendAvailability !== "future" && backendAvailability !== "admin");
    const isUnlocked = backendAvailability !== "future"
      && backendAvailability !== "admin"
      && (unlimitedOfferActive || unlockedResourceKeys.has(resourceKey));
    return {
      id,
      _id: id,
      productKey,
      key: resourceKey,
      isSystemManaged: resourceKey.toLowerCase() === "clientmcpserver",
      isSingleInstance: normalizeBoolean(resource.IsSingleInstance, resource.isSingleInstance),
      catalogViewGroupKey: normalizeString(resource.CatalogViewGroupKey, resource.catalogViewGroupKey),
      catalogPackageKey: normalizeString(resource.CatalogPackageKey, resource.catalogPackageKey),
      catalogPackageVersion: normalizeString(resource.CatalogPackageVersion, resource.catalogPackageVersion),
      catalogComparisonOrder: normalizeNumber(resource.CatalogComparisonOrder, resource.catalogComparisonOrder) || 0,
      isCatalogGroupRecommendation: normalizeBoolean(
        resource.IsCatalogGroupRecommendation,
        resource.isCatalogGroupRecommendation
      ),
      isCatalogUnavailablePreviewVisible: normalizeBoolean(
        resource.IsCatalogUnavailablePreviewVisible,
        resource.isCatalogUnavailablePreviewVisible
      ),
      name,
      ProductResourceDefinitionId: id,
      maturityStatus: normalizeNumber(resource.MaturityStatus, resource.maturityStatus) || 0,
      Icon: normalizeString(resource.Icon, resource.icon) || getResourceIconKey(resourceKey),
      IconPath: getResourceIconPath(resourceKey),
      availability: isUnlocked ? "unlocked" : backendAvailability,
      isUnlocked,
      isUnlimited,
      offers: getOffersForResource(resourceKey),
      summary: normalizeString(resource.Description, resource.description, resource.summary),
      slug: toMarketplaceSlug(name),
      MarketingOverviewHtml: normalizeString(resource.MarketingOverviewHtml, resource.marketingOverviewHtml),
      TechOverviewHtml: normalizeString(resource.TechOverviewHtml, resource.techOverviewHtml),
      MarketingUseCasesHtml: normalizeString(resource.MarketingUseCasesHtml, resource.marketingUseCasesHtml),
      PricingBasis: normalizeString(resource.PricingBasis, resource.pricingBasis),
      usagePricingDefault: normalizeUsagePricing(resource.UsagePricingDefault, resource.usagePricingDefault),
      supportedUsageModes: normalizeArray(resource.SupportedUsageModes, resource.supportedUsageModes)
        .map((mode) => normalizeNumber(mode))
        .filter((mode) => mode !== null),
      defaultUsageUnitMode: normalizeNumber(resource.DefaultUsageUnitMode, resource.defaultUsageUnitMode),
      usageCountingRule: normalizeString(resource.UsageCountingRule, resource.usageCountingRule),
      usageAttributionRequirement: normalizeString(
        resource.UsageAttributionRequirement,
        resource.usageAttributionRequirement
      ),
      ResourceConfigurationUnit: normalizeString(
        resource.ResourceConfigurationUnit,
        resource.resourceConfigurationUnit,
        product?.unit
      )
    };
  });

  const clientResources = rawClientResources.map((clientResource) => {
    const id = normalizeString(clientResource._id, clientResource.Id, clientResource.id, clientResource.UniqueId, clientResource.uniqueId);
    const productOfferKey = normalizeProductOfferKey(
      normalizeString(clientResource.ProductOfferKey, clientResource.productOfferKey)
    );
    return {
      id,
      _id: id,
      productKey: normalizeString(clientResource.ProductKey, clientResource.productKey),
      productResourceKey: normalizeString(clientResource.ProductResourceKey, clientResource.productResourceKey),
      productResourceDefinitionId: normalizeString(
        clientResource.ProductResourceDefinitionId,
        clientResource.productResourceDefinitionId
      ),
      displayName: normalizeString(clientResource.DisplayName, clientResource.displayName),
      productOfferKey,
      productOfferSku: normalizeProductOfferSku(
        normalizeString(clientResource.ProductOfferSku, clientResource.productOfferSku),
        productOfferKey
      ),
      lifecycleStatus: normalizeNumber(clientResource.LifecycleStatus, clientResource.lifecycleStatus),
      approvalStatus: normalizeNumber(clientResource.ApprovalStatus, clientResource.approvalStatus),
      effectiveAllowance: effectiveAllowanceByClientResourceId.get(id) || null,
      configurationCounts: clientResource.ResourceConfigurationCountSummary
        || clientResource.resourceConfigurationCountSummary
        || {}
    };
  }).filter((clientResource) => clientResource.productResourceKey);

  const resourceConfigurations = rawResourceConfigurations.map((configuration) => {
    const id = normalizeString(configuration._id, configuration.Id, configuration.id, configuration.UniqueId, configuration.uniqueId);
    const allowanceProductOfferKey = normalizeProductOfferKey(
      normalizeString(configuration.AllowanceProductOfferKey, configuration.allowanceProductOfferKey)
    );
    return {
      id,
      _id: id,
      clientResourceId: normalizeString(configuration.ClientResourceId, configuration.clientResourceId),
      productKey: normalizeString(configuration.ProductKey, configuration.productKey),
      productResourceKey: normalizeString(configuration.ProductResourceKey, configuration.productResourceKey),
      productResourceDefinitionId: normalizeString(
        configuration.ProductResourceDefinitionId,
        configuration.productResourceDefinitionId
      ),
      displayName: normalizeString(configuration.DisplayName, configuration.displayName),
      configurationStatus: normalizeNumber(configuration.ConfigurationStatus, configuration.configurationStatus),
      allowanceClientProductOfferId: normalizeString(
        configuration.AllowanceClientProductOfferId,
        configuration.allowanceClientProductOfferId
      ),
      allowanceProductOfferKey,
      allowanceProductOfferSku: normalizeProductOfferSku(
        normalizeString(configuration.AllowanceProductOfferSku, configuration.allowanceProductOfferSku),
        allowanceProductOfferKey
      ),
      allowanceGrantProductResourceKey: normalizeString(
        configuration.AllowanceGrantProductResourceKey,
        configuration.allowanceGrantProductResourceKey
      ),
      createdDate: normalizeString(configuration.CreatedDate, configuration.createdDate),
      updatedDate: normalizeString(configuration.UpdatedDate, configuration.updatedDate)
    };
  }).filter((configuration) => configuration.clientResourceId);

  const productsWithRuntimeCapacityOffers = attachRuntimeCapacityOffersToProducts(products, productOffers);

  return {
    categories: marketplaceCategories,
    products: productsWithRuntimeCapacityOffers,
    resources,
    productOffers,
    clientProductOffers,
    clientResources,
    resourceConfigurations,
    effectiveResourceAllowances,
    environmentKey: normalizeString(catalog?.EnvironmentKey, catalog?.environmentKey),
    marketplaceAccessMode,
    isSelfHostedResourceMode,
    isMarketplaceUnlimited: hasUnlimitedResourcesAccess,
    isMarketplaceAdmin: normalizeBoolean(catalog?.IsMarketplaceAdmin, catalog?.isMarketplaceAdmin)
  };
};

function buildResourceSummary(name, productKey) {
  if (name === "API Endpoint") {
    return "Expose selected schema entities, models, or read-only warehouse-native query capabilities through secured HTTP API routes. OpenAPI documentation is generated for enabled API deployments so callers can inspect the contract without treating documentation as a separate resource.";
  }
  if (name === "MCP Tool Endpoint") {
    return "Enable the general Convoke tools and knowledge MCP route for the client, with bearer-token access and discovery managed outside Schema API deployment records.";
  }
  if (name === "MCP Data Query Endpoint") {
    return "Expose governed data-query access to AI clients through MCP, limited to the selected schema/deployment scope and configured source or warehouse execution modes.";
  }
  if (name === "Client MCP Tools" || name === "Client Tools MCP") {
    return "Enable the general Convoke tools and knowledge MCP route for the client, with bearer-token access and discovery managed outside Schema API deployment records.";
  }
  const product = marketplaceProducts.find((item) => item.key === productKey);
  const productName = product?.name ?? "Product";
  return `${name} is a ${productName} option with setup, validation, and activation handled through Convoke.`;
}
